// src/lib/constants/legacyReports.ts
//
// Legacy Reports (/v1/reports1) selector vocabularies, query keys, and copy
// constants. Contract of record: ref_legacy_reports1_contract.md (JVM repo).

import { DateTime } from 'luxon';
import { SECONDS_PER_DAY, utcDateToSeconds } from '@/shared/utils/utcRange';

export const LEGACY_REPORT_SUBTYPES = [
  'timeseries',
  'geocountry',
  'georegion',
  'geocity',
  'embedurl',
] as const;
export type LegacyReportSubtype = typeof LEGACY_REPORT_SUBTYPES[number];

/** Wire values of the server-picked timeseries bucket unit (UnitOfTime.scala). */
export const LEGACY_UNIT_OF_TIME = ['s', 'mi', 'h', 'd', 'w', 'm', 'y', 'a', 'u'] as const;
export type LegacyUnitOfTime = typeof LEGACY_UNIT_OF_TIME[number];

/** Permanent methodology notice shown adjacent to the date controls (phase-plan requirement). */
export const LEGACY_METHODOLOGY_NOTICE =
  'Legacy Reports use the prior reporting methodology. Totals are not expected to match the new Analytics section.';

/**
 * Retained-range copy. The fixed end date becomes accurate only after
 * legacy_write_stop_utc (2027-01-01T00:00:00Z); Phase 6/7 flips this constant
 * to true instead of hunting copy (ref_analytics_product_transition.md).
 */
export const LEGACY_RETAINED_RANGE_COPY_ENABLED = false;
export const LEGACY_RETAINED_RANGE_COPY = 'Reports available through December 31, 2026.';

/**
 * ADR-011 boundary: the interactive Legacy Reports section queries UTC days through
 * this date INCLUSIVE (2026-07-07); the Analytics section floors at 2026-07-08 00:00Z.
 * The two are CONTIGUOUS — legacy's exclusive `to` ceiling below equals Analytics'
 * start instant, so there is no overlap and no gap. (The fleet-wide player flip is
 * 2026-07-08 05:00-07:00 UTC, so July 7 is legacy's last COMPLETE day.) SINGLE source
 * of truth for the client cap; the JVM server clamp (ADR-011 §3) uses the same
 * semantics (legacy ranges ≤ end of 2026-07-07). Analytics' floor is server-driven
 * (meta.customerStartDate = 2026-07-08). Does NOT affect Usage/resource_metric (ADR-012).
 */
export const LEGACY_REPORTS_QUERY_BOUNDARY_DATE = '2026-07-07';

/**
 * Exclusive `to` ceiling (UTC epoch seconds) for every interactive legacy query:
 * midnight AFTER the last covered legacy day (= Analytics' start instant,
 * 2026-07-08 00:00Z). Derived from the single boundary date above so it never drifts.
 */
export const LEGACY_REPORTS_QUERY_BOUNDARY_TO_SECONDS =
  (utcDateToSeconds(LEGACY_REPORTS_QUERY_BOUNDARY_DATE) ?? 0) + SECONDS_PER_DAY;

/** Legacy's last covered day, human-readable (e.g. "July 7, 2026"), single-sourced. */
export const LEGACY_REPORTS_QUERY_BOUNDARY_DISPLAY = DateTime.fromISO(
  LEGACY_REPORTS_QUERY_BOUNDARY_DATE,
  { zone: 'utc' }
).toFormat('LLLL d, yyyy');

/** The day Analytics begins (legacy boundary + 1), e.g. "July 8, 2026" — for the banner. */
export const ANALYTICS_START_DISPLAY = DateTime.fromISO(LEGACY_REPORTS_QUERY_BOUNDARY_DATE, {
  zone: 'utc',
})
  .plus({ days: 1 })
  .toFormat('LLLL d, yyyy');

/**
 * Hierarchical TanStack Query keys (ADR-003 §6) with the 'byCustomer' marker
 * for selected-customer invalidation. mediaIdsKey/subtypesKey are stable
 * stringified selector forms (sorted-joined, or 'all').
 */
export const LEGACY_REPORTS_QUERY_KEYS = {
  all: ['legacyReports'] as const,
  scoped: (cId: number) => [...LEGACY_REPORTS_QUERY_KEYS.all, 'byCustomer', cId] as const,
  account: (cId: number, start: number, end: number) =>
    [...LEGACY_REPORTS_QUERY_KEYS.scoped(cId), 'account', start, end] as const,
  subaccounts: (cId: number, start: number, end: number) =>
    [...LEGACY_REPORTS_QUERY_KEYS.scoped(cId), 'subaccounts', start, end] as const,
  medias: (cId: number, start: number, end: number, mediaIdsKey: string) =>
    [...LEGACY_REPORTS_QUERY_KEYS.scoped(cId), 'medias', start, end, mediaIdsKey] as const,
  mediasDetail: (cId: number, start: number, end: number, mediaIdsKey: string) =>
    [...LEGACY_REPORTS_QUERY_KEYS.scoped(cId), 'mediasDetail', start, end, mediaIdsKey] as const,
} as const;

/** Stable query-key form of an optional mediaIds selector. */
export function legacyMediaIdsKey(mediaIds?: number[] | null): string {
  if (!mediaIds || mediaIds.length === 0) return 'all';
  return [...mediaIds].sort((a, b) => a - b).join(',');
}
